import { AccountAiToolUsage, AccountInformation, AccountRoutesUsage, Bookmark, CreateAccountRequest, GameProgress, GetAccountResponse, GetOrganizationRoleResponse, LoginOrigin } from '../../types';
import { buildQueryString, clearAccountInformation, del, emptyPost, get, getAccountInformation, logWithAmplitude, put, throwHttpError } from './api';
import { getFromLocalStorage, isDefined, isValidString, removeLocalStorage, setLocalStorage } from '../utils';

import accountInfo from '../state/account-info';
import featuresInfo from '../state/features-info';
import { getDeviceName } from '../device';
import { persistSession } from '../auth-cookie';

export function saveEmail(email: string) {
    setLocalStorage('email', email);
}

function setAccountInformation(token: AccountInformation, organization_id: string | undefined = undefined) {
    setLocalStorage('accountInformation', JSON.stringify(token));
    void persistSession(token);
    removeLocalStorage('organization_id');
    if (isValidString(organization_id)) {
        setLocalStorage('organization_id', organization_id);
    }
    saveEmail(token.email);
}


export function redirectToLogin() {
    window.location.replace('/#login');
}

export async function login(amplitude_callsite: string, email: string, origin?: LoginOrigin, context_text?: string, context_icon?: string) {
    if (isDefined(context_icon) && context_icon.startsWith('/i/')) {
        context_icon = `${window.location.origin}${context_icon}`;
    }

    const queryString = buildQueryString({
        timestamp: new Date().toLocaleTimeString(),
        origin,
        context_text,
        context_icon
    });

    const response = await fetch(`/api/account/${email}/login${queryString}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while logging in.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Email Login', {
        email,
    });
}

export async function upgradeToken(
    amplitude_callsite: string,
    email: string,
    authentication_secret: string,
    organization_id: string | undefined,
    origin: LoginOrigin,
) {
    const device_name = await getDeviceName();
    const queryString = buildQueryString({
        device_name,
        authentication_secret,
        organization_id,
        origin
    });
    const response = await fetch(`/api/account/${email}/upgrade${queryString}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while upgrading the token.', response);
    }

    const accountInformation = (await response.json()) as AccountInformation;

    setAccountInformation(accountInformation, organization_id);
    setLocalStorage('authentication_secret', authentication_secret); // so we can check on click whether the secret link matches

    logWithAmplitude(amplitude_callsite, 'Upgrade Token', { email, organization_id });
    return accountInformation;
}


export async function getOrganizationRoles(
    amplitude_callsite: string,
    email: string,
) {
    if (!email) return [];
    const response = await fetch(`/api/account/${email}/roles/organization`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the organization roles.', response);
    }

    const rolesData = await response.json() as Promise<GetOrganizationRoleResponse[]>;

    logWithAmplitude(amplitude_callsite, 'Get Organization Rules', { email });
    return await rolesData;
}

export async function upgradeFacebookToken(
    amplitude_callsite: string,
    email: string,
    facebook_token: string
) {
    const device_name = await getDeviceName();
    const queryString = buildQueryString({
        device_name,
        facebook_token,
    });
    const response = await fetch(`/api/account/${email}/facebooklogin${queryString}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while upgrading the Facebook token.', response);
    }

    const accountInformation = (await response.json()) as AccountInformation;

    setAccountInformation(accountInformation);

    logWithAmplitude(amplitude_callsite, 'Upgrade Facebook Token', {
        email,
    });
    return accountInformation;
}

export async function addBookmark(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const account = await getAccount(amplitude_callsite);
    if (account === undefined) {
        return;
    }
    const newBookmark: Bookmark = {
        topic_id: { $oid: topic_id },
        topic_key,
    };
    account.bookmarks.push(newBookmark);
    await setAccount(amplitude_callsite, account, {}, LoginOrigin.Viewer);
    logWithAmplitude(amplitude_callsite, 'Bookmark Map', {
        email: account.email,
        full_name: account.full_name,
        num_bookmarks: account.bookmarks.length,
        bookmark: topic_id,
    });
}

export async function removeBookmark(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const account = await getAccount(amplitude_callsite);
    if (account === undefined) {
        return;
    }
    const bookmarkToRemove: Bookmark = {
        topic_id: { $oid: topic_id },
        topic_key,
    };
    account.bookmarks = account.bookmarks.filter(bookmark => bookmark.topic_id.$oid !== bookmarkToRemove.topic_id.$oid);

    await setAccount(amplitude_callsite, account, {}, LoginOrigin.Viewer);
    logWithAmplitude(amplitude_callsite, 'Remove Bookmark', {
        email: account.email,
        full_name: account.full_name,
        num_bookmarks: account.bookmarks.length,
        bookmark: topic_id,
    });
}

export async function addProfileFollow(
    amplitude_callsite: string,
    creator_profile_id: string,
) {
    const account = await getAccount(amplitude_callsite);
    if (account === undefined) {
        return;
    }
    account.followed_profiles.push({ $oid: creator_profile_id });
    await setAccount(amplitude_callsite, account, {}, LoginOrigin.Viewer);
    logWithAmplitude(amplitude_callsite, 'Follow Profile', {
        email: account.email,
        full_name: account.full_name,
        num_followed_profiles: account.followed_profiles.length,
        followed_profile: creator_profile_id,
    });
}

export async function removeProfileFollow(
    amplitude_callsite: string,
    creator_profile_id: string,
) {
    const account = await getAccount(amplitude_callsite);
    if (account === undefined) {
        return;
    }
    account.followed_profiles = account.followed_profiles.filter(profile => profile.$oid !== creator_profile_id);
    await setAccount(amplitude_callsite, account, {}, LoginOrigin.Viewer);
    logWithAmplitude(amplitude_callsite, 'Follow Profile', {
        email: account.email,
        full_name: account.full_name,
        num_followed_profiles: account.followed_profiles.length,
        followed_profile: creator_profile_id,
    });
}

export async function getAccount(amplitude_callsite: string): Promise<GetAccountResponse | undefined> {
    const accountInformation = getAccountInformation();
    if (accountInformation === undefined) {
        return undefined;
    }

    const response = await fetch(`/api/account/${accountInformation.email}`, get());

    if (response.status === 401) {
        // The stored session is invalid/expired. Clear it so its stale credentials don't poison later
        // requests (e.g. a topic load) and strand the visitor on the global login page.
        clearAccountInformation();
        return undefined;
    }

    if (!response.ok) {
        return undefined;
    }

    const account = (await response.json()) as GetAccountResponse;

    logWithAmplitude(amplitude_callsite, 'Get Account', {
        email: account.email,
        full_name: account.full_name,
        num_bookmarks: account.bookmarks.length,
    });
    return account;
}

export async function setAccount(
    amplitude_callsite: string,
    account: CreateAccountRequest,
    additional_logged_parameters: Object = {},
    // Pass the calling surface so participant/viewer updates (e.g. a challenge
    // check-in editing the user's name) don't mint a Klaviyo profile. Omit for
    // admin account edits.
    origin?: LoginOrigin
) {
    const queryString = buildQueryString({ origin });
    const response = await fetch(`/api/account/${account.email}${queryString}`, put(account));

    if (!response.ok) {
        await throwHttpError('An error occurred while updating the account.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Set Account',
        Object.assign(
            {},
            {
                email: account.email,
                full_name: account.full_name,
                num_bookmarks: account.bookmarks.length,
            },
            additional_logged_parameters
        )
    );
}

export async function upgradeGoogleToken(
    amplitude_callsite: string,
    email: string,
    google_token: string
) {
    const device_name = await getDeviceName();
    const queryString = buildQueryString({
        device_name,
        google_token,
    });
    const response = await fetch(`/api/account/${email}/googlelogin${queryString}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while upgrading the Google token.', response);
    }

    const accountInformation = (await response.json()) as AccountInformation;

    setAccountInformation(accountInformation);

    logWithAmplitude(amplitude_callsite, 'Upgrade Google Token', {
        email,
    });
    return accountInformation;
}

export async function logout(amplitude_callsite: string, everywhere: boolean = false) {
    const accountInformation = getAccountInformation();
    logWithAmplitude(amplitude_callsite, 'Logout', {
        email: accountInformation?.email,
    });
    if (accountInformation === undefined) {
        throw new Error('You must be logged in to log out.');
    }

    const queryString = buildQueryString({
        device_name: everywhere ? undefined : accountInformation.device_name,
    });
    await fetch(
        `/api/account/${accountInformation.email}/token${queryString}`,
        del()
    );

    clearAccountInformation();
}

export async function deleteAccount(amplitude_callsite: string) {
    const accountInformation = getAccountInformation();
    logWithAmplitude(amplitude_callsite, 'Delete Account', {
        email: accountInformation?.email,
    });
    if (accountInformation === undefined) {
        throw new Error('You must be logged in to delete account.');
    }

    const response = await fetch(`/api/account/${accountInformation.email}`, del());

    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the account.', response);
    }

    clearAccountInformation();
}

export async function tryOrRedirectUnauthorized<R>(func: (() => Promise<R>) | (() => R)) {
    try {
        // This must await, as an exception thrown by the Promise will otherwise not be caught in the try-catch
        return await Promise.resolve(func());
    } catch (e) {
        if (getAccountInformation() === undefined) {
            window.location.replace(
                `/#login?callback_url=${encodeURIComponent(window.location.href)}`
            );
        } else {
            window.location.replace('/#unauthorized');
        }
    }
}

// functions to track usage of AI tools

export function incrementMonth(date: Date): Date {
    const nextMonth = new Date(date);
    nextMonth.setMonth(date.getMonth() + 1);
    return nextMonth;
}

export function areDatesInSameMonth(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
}

export async function iterateAICount(amplitude_callsite: string) {

    const account = await getAccount(amplitude_callsite);

    if (account === undefined) {
        // add it to local storage
        const uses = getFromLocalStorage('ai_tool_uses_this_month') ?? undefined;
        if (uses === undefined) {
            setLocalStorage('ai_tool_uses_this_month', (1).toString());
        } else {
            setLocalStorage('ai_tool_uses_this_month', (parseInt(uses) + 1).toString());
        }
        return;
    }
    const response = await fetch(`/api/account/${account.email}/add_ai_tool_usage`, emptyPost());

    if (!response.ok) {
        console.error('An error occurred while updating ai_tool_uses.', response);
        return account;
    }

    const updatedAccount = (await response.json()) as GetAccountResponse;

    accountInfo.ai_tool_uses_this_month = updatedAccount.ai_tool_uses?.num_ai_uses_month ?? 0;
}

export async function checkIfAIEligible(amplitude_callsite: string): Promise<boolean> {

    const account = await getAccount(amplitude_callsite);

    if (account?.ai_tool_uses === undefined) {
        return true;
    }

    // Convert savedDate and currentDate to Date objects
    const currentCalls = account?.ai_tool_uses?.num_ai_uses_month ?? 0;

    const savedDateObj = new Date(account?.ai_tool_uses?.ai_use_startdate);
    const currentDateObj = new Date();
    // Check if the savedDate is in the same month as currentDate
    if (areDatesInSameMonth(savedDateObj, currentDateObj)) {
        // If yes, the calls are in the same month
        if (currentCalls >= featuresInfo.numberOfUsesOfProxiAIEnabled) {
            return false; // User has reached the maximum calls for the month
        } else {
            return true; // User is eligible to create a new call
        }
    } else {
        // If not, it's a new month, so the user is eligible to create a new call
        return true;
    }
}

export async function incrementRouteCount(amplitude_callsite: string) {
    const account = await getAccount(amplitude_callsite);

    if (account === undefined) {
        // add it to local storage
        const uses = getFromLocalStorage('route_uses_this_month') ?? undefined;
        if (uses === undefined) {
            setLocalStorage('route_uses_this_month', (1).toString());
        } else {
            setLocalStorage('route_uses_this_month', (parseInt(uses) + 1).toString());
        }
        return;
    }
    const response = await fetch(`/api/account/${account.email}/add_route_usage`, emptyPost());

    if (!response.ok) {
        console.error('An error occurred while updating route_uses.', response);
        return account;
    }

    const updatedAccount = (await response.json()) as GetAccountResponse;

    accountInfo.routes_uses_this_month = updatedAccount.routes_uses?.num_routes_uses_month ?? 0;
}

export async function checkIfRouteEligible(amplitude_callsite: string): Promise<boolean> {

    const account = await getAccount(amplitude_callsite);

    if (account?.routes_uses === undefined) {
        return true;
    }

    // Convert savedDate and currentDate to Date objects
    const currentCalls = account?.routes_uses?.num_routes_uses_month ?? 0;

    const savedDateObj = new Date(account?.routes_uses?.routes_use_startdate);
    const currentDateObj = new Date();
    // Check if the savedDate is in the same month as currentDate
    if (areDatesInSameMonth(savedDateObj, currentDateObj)) {
        // If yes, the calls are in the same month
        if (currentCalls >= featuresInfo.numberOfUsesOfRoutesEnabled) {
            return false; // User has reached the maximum calls for the month
        } else {
            return true; // User is eligible to create a new call
        }
    } else {
        // If not, it's a new month, so the user is eligible to create a new call
        return true;
    }
}

// update account game progress
export async function updateAccountGameProgress(email: string, gameProgress: GameProgress) {
    const response = await fetch(`/api/game/account_progress/${email}`, put(gameProgress));

    if (!response.ok) {
        await throwHttpError('An error occurred while updating the account game progress.', response);
    }
}

// HELPER FUNCTIONS
export function aIUsesThisMonth(uses: AccountAiToolUsage | undefined): number {
    // Convert savedDate and currentDate to Date objects
    const currentCalls = uses?.num_ai_uses_month ?? parseInt(getFromLocalStorage('ai_tool_uses_this_month') ?? '0');

    const savedDateObj = new Date(uses?.ai_use_startdate ?? getFromLocalStorage('ai_tool_uses_startdate') ?? new Date());

    const currentDateObj = new Date();

    // Check if the savedDate is in the same month as currentDate
    if (areDatesInSameMonth(savedDateObj, currentDateObj)) {
        // If yes, the calls are in the same month
        if (currentCalls >= featuresInfo.numberOfUsesOfProxiAIEnabled) {
            return currentCalls; // User has reached the maximum calls for the month
        } else {
            return currentCalls; // User is eligible to create a new call
        }
    } else {
        // If not, it's a new month, so the user is eligible to create a new call
        return 0;
    }
}


export function routeUsesThisMonth(uses: AccountRoutesUsage | undefined): number {
    // Convert savedDate and currentDate to Date objects
    const currentCalls = uses?.num_routes_uses_month ?? parseInt(getFromLocalStorage('route_uses_this_month') ?? '0');

    const savedDateObj = new Date(uses?.routes_use_startdate ?? getFromLocalStorage('route_uses_startdate') ?? new Date());

    const currentDateObj = new Date();

    // Check if the savedDate is in the same month as currentDate
    if (areDatesInSameMonth(savedDateObj, currentDateObj)) {
        // If yes, the calls are in the same month
        if (currentCalls >= featuresInfo.numberOfUsesOfProxiAIEnabled) {
            return currentCalls; // User has reached the maximum calls for the month
        } else {
            return currentCalls; // User is eligible to create a new call
        }
    } else {
        // If not, it's a new month, so the user is eligible to create a new call
        return 0;
    }
}